import { TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineHeading } from '../../base/outline-heading/outline-heading';
import { html, unsafeStatic } from 'lit/static-html.js';
import { classMap } from 'lit/directives/class-map.js';

import componentStyles from './se-heading.css.lit';

export const allowedHeadingFonts = ['dmsans', 'rift'] as const;

export type HeadingFont = typeof allowedHeadingFonts[number];

export const headingColors = [
  'primary-1',
  'primary-2',
  'secondary-1',
  'secondary-2',
] as const;

export type HeadingColor = typeof headingColors[number];

/**
 * The Heading component.
 * @element se-heading
 * @slot defaultSlot
 */
@customElement('se-heading')
export class SeHeading extends OutlineHeading {
  static styles = [componentStyles];

  /**
   * Overrides default font styles of H1: rift, H2-H6: DM Sans
   */
  @property({ type: String })
  font: HeadingFont;

  /**
   * Adds first two recognized primary and secondary brand colors to heading if set.
   */
  @property({ type: String })
  color: HeadingColor;

  render(): TemplateResult {
    const classes = {
      'se-text': !this.level,
      [`${this.font}`]: this.font,
      [`se-text--${this.levelSize}`]: this.levelSize,
      [`se-font--${this.levelStyle}`]: this.levelStyle,
      'alt-color': this.color,
    };
    return html`
      <${unsafeStatic(this.level as string)} class=${classMap(classes)}>
        <slot></slot>
      </${unsafeStatic(this.level as string)}>`;
  }

  firstUpdated() {
    this.setCSSVars();
  }

  setCSSVars() {
    if (this.color) {
      this.style.setProperty('--alt-color-value', `var(--brand-${this.color})`);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-heading': SeHeading;
  }
}
